<template>
  <button class="btn" :class="classes">
    <div
      v-if="props.loading"
      :class="[props.size === 'small' ? 'w-5 h-5' : 'w-6 h-6']"
      class="i-svg-spinners:3-dots-fade absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
    ></div>
    <template v-if="props.icon">
      <svg-icon :name="props.icon" class="flex-shrink-0" :class="svgClasses" :color="props.iconColor" />
    </template>
    <div v-if="props.type !== 'icon'" class="flex-0" :class="textClasses"><slot>Button</slot></div>
  </button>
</template>

<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { twMerge } from 'tailwind-merge'

import { BUTTON_SIZE_CLASSES, BUTTON_SVG_SIZE_CLASSES, buttonVariants } from '@/constants/button'
import type { BtnType, ButtonSize } from '@/types/component'

interface Props {
  type?: BtnType
  size?: ButtonSize
  icon?: string
  iconPosition?: 'left' | 'right'
  iconColor?: string
  textClass?: string
  block?: boolean
  bordered?: boolean
  loading?: boolean
  truncateText?: boolean
  class?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
  type: 'default',
  size: 'medium',
  iconPosition: 'left',
  iconColor: 'black',
  block: false,
  bordered: false,
  loading: false,
  truncateText: false,
})

const btnVariantClasses = computed(() => buttonVariants({ type: props.type, size: props.size }))
const classes = computed(() =>
  twMerge(
    `btn-${props.type}`,
    BUTTON_SIZE_CLASSES[props.size],
    btnVariantClasses.value,
    props.icon && props.iconPosition === 'right' && 'flex-row-reverse',
    props.block && 'w-full',
    props.bordered && 'border-1!',
    props.class
  )
)

const svgClasses = computed(() => [
  `svg-icon-${props.icon}`,
  BUTTON_SVG_SIZE_CLASSES[props.size] || BUTTON_SVG_SIZE_CLASSES.default,
  { 'opacity-0': props.loading },
  { 'flex-0': props.type !== 'icon' },
])

const lineClampClasses = computed(() => (props.truncateText ? ['whitespace-normal', 'line-clamp-1'] : ['whitespace-nowrap']))

const textClasses = computed(() => [{ 'opacity-0': props.loading }, lineClampClasses.value, props.textClass])
</script>
